import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  LinearProgress,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Money as MoneyIcon,
  Palette as PaletteIcon,
  Architecture as ArchitectureIcon,
  Agriculture as AgricultureIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Security as SecurityIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Assignment as AssignmentIcon,
  Token as TokenIcon,
  SmartToy as SmartToyIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { Project } from '../services/projectService';

interface ProjectDetailsModalProps {
  open: boolean;
  onClose: () => void;
  project: Project | null;
}

const ProjectDetailsModal: React.FC<ProjectDetailsModalProps> = ({ open, onClose, project }) => {
  if (!project) return null;

  const getProjectTypeIcon = (type: string) => {
    switch (type) {
      case 'real_estate': return <BusinessIcon />;
      case 'invoice': return <MoneyIcon />;
      case 'art': return <PaletteIcon />;
      case 'equipment': return <ArchitectureIcon />;
      case 'agriculture': return <AgricultureIcon />;
      default: return <BusinessIcon />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'submitted': return 'warning';
      case 'approved': return 'info';
      case 'tokenized': return 'success';
      case 'rejected': return 'error';
      case 'draft': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'submitted': return <AssignmentIcon />;
      case 'approved': return <CheckCircleIcon />;
      case 'tokenized': return <TokenIcon />;
      case 'rejected': return <WarningIcon />;
      case 'draft': return <InfoIcon />;
      default: return <InfoIcon />;
    }
  };

  const getRiskColor = (score: number) => {
    if (score > 70) return 'error';
    if (score > 40) return 'warning';
    return 'success';
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Not available';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: 'rgba(17, 24, 39, 0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }
      }}
    >
                   <DialogTitle sx={{ 
               background: 'linear-gradient(135deg, #8B5CF6, #3B82F6)',
               backgroundClip: 'text',
               WebkitBackgroundClip: 'text',
               WebkitTextFillColor: 'transparent',
               fontWeight: 700,
               display: 'flex',
               alignItems: 'center',
               gap: 2
             }}>
               <Avatar
                 sx={{
                   bgcolor: 'primary.main',
                   width: 40,
                   height: 40,
                   background: 'linear-gradient(135deg, #8B5CF6, #3B82F6)',
                 }}
               >
          {getProjectTypeIcon(project.project_type)}
        </Avatar>
        Project Details
      </DialogTitle>
      
      <DialogContent>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Project Header */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              {project.title || 'Untitled Project'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Chip
                icon={getStatusIcon(project.status)}
                label={project.status}
                color={getStatusColor(project.status) as any}
                sx={{ 
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: 'capitalize'
                }}
              />
              <Chip
                label={project.project_type.replace('_', ' ')}
                color="primary"
                variant="outlined"
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'capitalize'
                }}
              />
            </Box>
            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              {project.description || 'No description available'}
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12} md={6}>
              <Card sx={{ 
                background: 'rgba(17, 24, 39, 0.5)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 2
              }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InfoIcon color="primary" />
                    Basic Information
                  </Typography>
                  
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <MoneyIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Valuation"
                        secondary={`$${project.valuation_usd?.toLocaleString() || 'N/A'}`}
                        secondaryTypographyProps={{ color: 'primary.main', fontWeight: 600 }}
                      />
                    </ListItem>
                    
                    <ListItem>
                      <ListItemIcon>
                        <TrendingUpIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Tokenized Percentage"
                        secondary={`${project.tokenized_percent || 0}%`}
                        secondaryTypographyProps={{ color: 'success.main', fontWeight: 600 }}
                      />
                    </ListItem>
                    
                    {project.location && (
                      <ListItem>
                        <ListItemIcon>
                          <LocationIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Location"
                          secondary={project.location}
                        />
                      </ListItem>
                    )}
                    
                    <ListItem>
                      <ListItemIcon>
                        <CalendarIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Created"
                        secondary={formatDate(project.created_at)}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            {/* Token Information */}
            <Grid item xs={12} md={6}>
              <Card sx={{ 
                background: 'rgba(17, 24, 39, 0.5)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 2
              }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TokenIcon color="primary" />
                    Token Information
                  </Typography>
                  
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <TokenIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Token Name"
                        secondary={project.token_name || 'Not set'}
                      />
                    </ListItem>
                    
                    <ListItem>
                      <ListItemIcon>
                        <TokenIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Token Symbol"
                        secondary={project.token_symbol || 'Not set'}
                      />
                    </ListItem>
                    
                    <ListItem>
                      <ListItemIcon>
                        <SmartToyIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Smart Contract"
                        secondary={project.smart_contract_address || 'Not deployed'}
                        secondaryTypographyProps={{ 
                          fontFamily: 'monospace',
                          fontSize: '0.75rem'
                        }}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            {/* Risk Assessment */}
            {project.risk_score && (
              <Grid item xs={12}>
                <Card sx={{ 
                  background: 'rgba(17, 24, 39, 0.5)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: 2
                }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SecurityIcon color="primary" />
                      Risk Assessment
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Overall Risk Score
                            </Typography>
                            <Typography variant="body2" fontWeight={600} color={getRiskColor(project.risk_score.score)}>
                              {project.risk_score.score}/100
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={project.risk_score.score}
                            color={getRiskColor(project.risk_score.score) as any}
                            sx={{
                              height: 8,
                              borderRadius: 4,
                              bgcolor: 'rgba(255, 255, 255, 0.1)',
                            }}
                          />
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Liquidity Score
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {project.risk_score.liquidity_score}/100
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={project.risk_score.liquidity_score}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              bgcolor: 'rgba(255, 255, 255, 0.1)',
                              '& .MuiLinearProgress-bar': {
                                background: 'linear-gradient(90deg, #2196F3, #00BCD4)',
                                borderRadius: 3,
                              }
                            }}
                          />
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Credit Score
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {project.risk_score.credit_score}/100
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={project.risk_score.credit_score}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              bgcolor: 'rgba(255, 255, 255, 0.1)',
                              '& .MuiLinearProgress-bar': {
                                background: 'linear-gradient(90deg, #4CAF50, #8BC34A)',
                                borderRadius: 3,
                              }
                            }}
                          />
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Volatility Score
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {project.risk_score.volatility_score}/100
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={project.risk_score.volatility_score}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              bgcolor: 'rgba(255, 255, 255, 0.1)',
                              '& .MuiLinearProgress-bar': {
                                background: 'linear-gradient(90deg, #FF9800, #FF5722)',
                                borderRadius: 3,
                              }
                            }}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                    
                    {project.risk_score.model_version && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255, 255, 255, 0.05)', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Model Version: {project.risk_score.model_version}
                          {project.risk_score.generated_at && ` • Generated: ${formatDate(project.risk_score.generated_at)}`}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Project ID */}
            <Grid item xs={12}>
              <Box sx={{ p: 2, bgcolor: 'rgba(255, 255, 255, 0.05)', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Project ID
                </Typography>
                <Typography variant="body2" fontFamily="monospace" color="primary.main">
                  {project.id}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </motion.div>
      </DialogContent>
      
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectDetailsModal; 